import { useAppDispatch, useAppSelector } from 'modules/hooks/appHooks';
import { useLocationCodes } from 'modules/hooks/codesHooks/useLocationCodes';
import {
  LoginSelectors,
  LoginThunks,
} from 'modules/landingPage/components/login/redux';
import { useToastContainer } from 'modules/layout/toastContainer';
import { ProfileSelectors, ProfileThunks } from 'modules/profilePage/redux';
import { InputWithLabel, SelectWithLabel } from 'modules/ui';
import { ToastType } from 'modules/ui/toast';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormButtons, FormContainer } from 'modules/ui/form';
import { PHONE_NUMBER_PREFIX } from 'modules/hooks/validationHooks';
import { UpdateUserData } from 'modules/types/User';
import { MaxFieldLength } from '../../../ui/input/common/MaxFieldLength';
import { ValidationType } from '../../../../types/ValidationType';
import { useForm } from '../../../ui/form/useForm';
import { getDefaultUserUpdateData } from '../helper';

export const EditProfileForm: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { addToast } = useToastContainer();

  const [validate, setValidate] = useState<boolean>(false);
  const { validateForm } = useForm();

  const user = useAppSelector(LoginSelectors.selectUser);
  const updateAddressSuccess = useAppSelector(
    ProfileSelectors.selectUpdateProfileDataSuccess,
  );
  const updateAddressError = useAppSelector(
    ProfileSelectors.selectUpdateProfileDataError,
  );
  const { countryOptions, cityAndZipCodeOptions } = useLocationCodes();

  const [shippingData, setShippingData] = useState<UpdateUserData>(
    getDefaultUserUpdateData(user),
  );
  const [buttonClicked, setButtonClicked] = useState(false);

  useEffect(() => {
    if (updateAddressSuccess) {
      dispatch(LoginThunks.fetchUser);
    }
  }, [dispatch, updateAddressSuccess]);

  useEffect(() => {
    if (updateAddressSuccess && buttonClicked) {
      addToast({
        title: t('profilePage.dataModification.info'),
        text: t('profilePage.dataModification.successMessage'),
      });
      setButtonClicked(false);
    } else if (updateAddressError && buttonClicked) {
      addToast({
        title: t('profilePage.dataModification.info'),
        text: t('profilePage.dataModification.errorMessage'),
        type: ToastType.Urgent,
      });
      setButtonClicked(false);
    }
  }, [addToast, t, updateAddressError, updateAddressSuccess, buttonClicked]);

  const handleShippingAddressUpdate = (event: any) => {
    event.preventDefault();
    setValidate(true);

    if (!validateForm(event)) {
      setButtonClicked(true);
      dispatch(ProfileThunks.updateProfileData(shippingData));
    }
  };

  return (
    <FormContainer noValidate onSubmit={handleShippingAddressUpdate}>
      <SelectWithLabel
        required
        label={t('profilePage.dataModification.shippingCountry')}
        placeholderText={t(
          'profilePage.dataModification.shippingCountryPlaceholder',
        )}
        options={countryOptions}
        defaultValue={shippingData.shippingCountry}
        onChange={(value) =>
          setShippingData({
            ...shippingData,
            shippingCountry: value,
          })
        }
        validators={[ValidationType.REQUIRED]}
        validate={validate}
      />
      <InputWithLabel
        required
        label={t('profilePage.dataModification.shippingStreet')}
        placeholderText={t(
          'profilePage.dataModification.shippingStreetPlaceholder',
        )}
        defaultValue={shippingData.shippingStreet}
        onBlur={(e) =>
          setShippingData({
            ...shippingData,
            shippingStreet: e.target.value,
          })
        }
        maxLength={MaxFieldLength.StreetName}
        validators={[ValidationType.REQUIRED]}
        validate={validate}
      />
      <InputWithLabel
        required
        label={t('profilePage.dataModification.shippingHouseNumber')}
        placeholderText={t(
          'profilePage.dataModification.shippingHouseNumberPlaceholder',
        )}
        defaultValue={shippingData.shippingHouseNumber}
        onBlur={(e) =>
          setShippingData({
            ...shippingData,
            shippingHouseNumber: e.target.value,
          })
        }
        maxLength={MaxFieldLength.HouseNumber}
        validators={[ValidationType.REQUIRED]}
        validate={validate}
      />
      <SelectWithLabel
        required
        label={t('profilePage.dataModification.shippingCity')}
        placeholderText={t(
          'profilePage.dataModification.shippingCityPlaceholder',
        )}
        defaultValue={shippingData.shippingPostalCode}
        options={cityAndZipCodeOptions}
        onChange={(value) => {
          setShippingData({
            ...shippingData,
            shippingPostalCode: value,
          });
        }}
        validators={[ValidationType.REQUIRED]}
        validate={validate}
      />
      <InputWithLabel
        label={t('profilePage.dataModification.phone')}
        placeholderText={t('profilePage.dataModification.phonePlaceholder')}
        defaultValue={shippingData.phone?.number}
        onBlur={(e) =>
          setShippingData({
            ...shippingData,
            phone: {
              areaCode: shippingData.phone?.areaCode || PHONE_NUMBER_PREFIX,
              number: e.target.value,
            },
          })
        }
        type="tel"
        maxLength={MaxFieldLength.Phone}
        prefix={PHONE_NUMBER_PREFIX}
        validators={[ValidationType.PHONE]}
        validate={validate}
      />
      <InputWithLabel
        required
        label={t('profilePage.dataModification.shippingEmail')}
        placeholderText={t('profilePage.dataModification.emailPlaceholder')}
        defaultValue={shippingData.email}
        onBlur={(e) =>
          setShippingData({
            ...shippingData,
            email: e.target.value,
          })
        }
        type="email"
        maxLength={MaxFieldLength.Email}
        validators={[ValidationType.REQUIRED, ValidationType.EMAIL]}
        validate={validate}
      />

      <FormButtons text={t('profilePage.save')} />
    </FormContainer>
  );
};
